import type { RewardType } from '../../data/schema';
import type { CoreEvent, Monster, Player } from '../types';
import type { World } from '../world';

/** Compteurs d'une run, pour le score et les succès. Ils observent la simulation sans jamais l'influencer. */
export interface RunStats {
  damageDealt: number; damageTaken: number; roomsWithoutDamage: number; bossKillMs: number | null; bossSeen: boolean;
  kamasEarned: number; kamasSpent: number; itemsBought: number; maxSetPieces: number; spellsLearned: number; maxSpellLevel: number;
  cardsTaken: number; rerollsUsed: number; monstersSlowed: number; killsByDefId: Record<string, number>; maxKillsIn5s: number;
  levelReached: number; hpMinPct: number; defisCleared: number; teleports: number; petKills: number;
}

export function emptyStats(): RunStats {
  return {
    damageDealt: 0, damageTaken: 0, roomsWithoutDamage: 0, bossKillMs: null, bossSeen: false, kamasEarned: 0, kamasSpent: 0, itemsBought: 0,
    maxSetPieces: 0, spellsLearned: 0, maxSpellLevel: 0, cardsTaken: 0, rerollsUsed: 0, monstersSlowed: 0, killsByDefId: {}, maxKillsIn5s: 0,
    levelReached: 0, hpMinPct: 1, defisCleared: 0, teleports: 0, petKills: 0,
  };
}

export interface StatsTracker {
  stats: RunStats;
  /** À appeler une fois par pas avec tous les événements du pas (commandes comprises) ; met aussi à jour les maxima du joueur. */
  onEvents(events: CoreEvent[], world: World, player: Player, playerId: number): void;
  onRoomEntered(isBoss: boolean, reward: RewardType | null, time: number): void;
  onRoomCleared(time: number): void;
}

/** Fenêtre glissante (s) du compteur de morts rapprochées. */
const KILL_WINDOW = 5;

/** Plus grand nombre de pièces portées appartenant à une même panoplie. */
function setPieces(world: World, p: Player): number {
  const worn = Object.values(p.equipment).filter((id): id is string => !!id);
  let best = 0;
  for (const set of Object.values(world.content.sets)) best = Math.max(best, worn.filter((id) => set.items.includes(id)).length);
  return best;
}

export function createStatsTracker(): StatsTracker {
  const stats = emptyStats();
  const killTimes: number[] = [];
  let hurtThisRoom = false;
  let roomReward: RewardType | null = null;
  let bossEnteredAt = 0;

  return {
    stats,
    onRoomEntered(isBoss, reward, time) {
      hurtThisRoom = false;
      roomReward = reward;
      if (isBoss) { stats.bossSeen = true; bossEnteredAt = time; }
    },
    onRoomCleared() {
      if (!hurtThisRoom) stats.roomsWithoutDamage++;
      if (roomReward === 'defi') stats.defisCleared++;
    },
    onEvents(events, world, player, playerId) {
      let gearChanged = false;
      for (const e of events) {
        if (e.type === 'damage') {
          if (e.targetId === playerId) { if (e.amount > 0) { stats.damageTaken += e.amount; hurtThisRoom = true; } }
          // les coups reçus par les poupées (invocations du joueur) ne sont pas des dégâts infligés
          else if (world.entities.get(e.targetId)?.kind !== 'summon') stats.damageDealt += e.amount;
        } else if (e.type === 'death') {
          // ni la mort du joueur ni celle d'une poupée ne sont des monstres tués
          if (e.entityId === playerId || world.content.summons[e.defId]) continue;
          if (e.by === 'pet') stats.petKills++;
          stats.killsByDefId[e.defId] = (stats.killsByDefId[e.defId] ?? 0) + 1;
          killTimes.push(world.time);
          while (killTimes.length && killTimes[0]! < world.time - KILL_WINDOW) killTimes.shift();
          stats.maxKillsIn5s = Math.max(stats.maxKillsIn5s, killTimes.length);
          const m = world.entities.get(e.entityId) as Monster | undefined;
          if (m?.boss && stats.bossKillMs === null) stats.bossKillMs = Math.round((world.time - bossEnteredAt) * 1000);
        } else if (e.type === 'pickup') {
          if (e.pickupType === 'kamas') stats.kamasEarned += e.amount;
        } else if (e.type === 'bought') {
          stats.itemsBought++; stats.kamasSpent += e.price; gearChanged = true;
        } else if (e.type === 'shopRerolled') {
          stats.kamasSpent += e.price;
        } else if (e.type === 'itemGranted') {
          gearChanged = true;
        } else if (e.type === 'cardChosen') {
          stats.cardsTaken++;
          if (e.cardId.startsWith('spell:')) stats.spellsLearned++;
        } else if (e.type === 'status') {
          if (e.status === 'slow') stats.monstersSlowed++;
        } else if (e.type === 'blink') {
          stats.teleports++;
        }
      }
      if (gearChanged) stats.maxSetPieces = Math.max(stats.maxSetPieces, setPieces(world, player));
      stats.levelReached = player.level;
      if (player.hpMax > 0) stats.hpMinPct = Math.min(stats.hpMinPct, player.hp / player.hpMax);
      for (const s of [player.primary, ...player.spells]) stats.maxSpellLevel = Math.max(stats.maxSpellLevel, s.level);
    },
  };
}
